import { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
  useGetPlansQuery,
} from "../../redux/api";
import type { Organization } from "../../redux/api";
import { validatePhone } from "../../utils/validation";
import { PhoneInput } from "../../components/PhoneInput";
import { Field } from "../../components/Field";
import { useImageUpload } from "../../hooks/useImageUpload";
import { GalleryPickerModal } from "../../components/GalleryPickerModal";
import { ImagePreviewModal } from "../../components/ImagePreviewModal";
import { ImageEditorModal } from "../../components/ImageEditorModal";
import type { Asset } from "../../redux/api";

type OrgForm = {
  name: string;
  status: string;
  planId: string;
  industry: string;
  country: string;
  currency: string;
  website: string;
  contact: string;
  timezone: string;
  dateformat: string;
  timeFormat: string;
};

// Org-wide display preferences — every member of the organization sees dates and
// times in these formats.
const TIMEZONES = [
  "Asia/Kolkata",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const DATE_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "DD MMM YYYY",
];

const emptyForm: OrgForm = {
  name: "",
  status: "active",
  planId: "",
  industry: "",
  country: "",
  currency: "",
  website: "",
  contact: "",
  timezone: "Asia/Kolkata",
  dateformat: "DD/MM/YYYY",
  timeFormat: "24",
};

const formFromOrg = (org?: Organization): OrgForm => ({
  ...emptyForm,
  name: org?.name ?? "",
  status: org?.status ?? "active",
  planId: org?.planId ?? "",
  timezone: org?.timezone ?? "Asia/Kolkata",
  dateformat: org?.dateformat ?? "DD/MM/YYYY",
  timeFormat: org?.timeFormat ?? "24",
});

// Organization → Setup. Owners pick one of their organizations and update its
// profile via the services `updateOrganization` mutation.
const OrganizationSetup = () => {
  const { data: organizations = [], isLoading } = useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();
  const [updateOrganization, { isLoading: saving }] =
    useUpdateOrganizationMutation();

  const [orgId, setOrgId] = useState<string>("");
  const [form, setForm] = useState<OrgForm>(emptyForm);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-field validation messages — shown directly under the offending input
  // (with a red border) instead of the single top-of-form banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const selected = organizations.find((o) => o.id === orgId);

  const { upload, uploading, error: uploadError } = useImageUpload("ORGANIZATION", orgId);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Saves immediately on upload (not part of the dirty-tracked form below) —
  // there's no "pending" state for a logo the way there is for text fields.
  const handleLogoFile = async (file: File | undefined) => {
    if (!file || !orgId) return;
    const uploaded = await upload(file);
    if (uploaded) {
      setMessage(null);
      setError(null);
      await updateOrganization({ id: orgId, input: { logoKey: uploaded.key } }).unwrap();
      setMessage("Logo updated.");
    }
  };

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const handlePickLogoFromGallery = async ([asset]: Asset[]) => {
    if (!orgId || !asset) return;
    setMessage(null);
    setError(null);
    await updateOrganization({ id: orgId, input: { logoKey: asset.key } }).unwrap();
    setMessage("Logo updated.");
  };

  // Points the organization at the edited copy. The original object survives
  // in the Gallery, so an unwanted edit is undone by picking it again.
  const handleLogoEdited = async (edited: { key: string }) => {
    if (!orgId) return;
    setMessage(null);
    setError(null);
    await updateOrganization({ id: orgId, input: { logoKey: edited.key } }).unwrap();
    setMessage("Logo updated.");
  };

  // Default to the first organization once the list loads. Only ever sets state
  // conditionally, so it can't loop on the fresh-[] default during loading.
  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  // Sync the form whenever the selected org changes (by id, or after a server
  // update bumps updatedAt). Depends on primitives — not the array reference —
  // to avoid re-running every render.
  useEffect(() => {
    setForm(formFromOrg(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.updatedAt]);

  const setField = (key: keyof OrgForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const baseline = useMemo(() => formFromOrg(selected), [selected]);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setMessage(null);
    setError(null);

    if (!orgId) return;

    const errors: Record<string, string> = {};
    if (!form.name.trim()) {
      errors.name = "Organization name is required.";
    }

    const phoneError = validatePhone(form.contact);
    if (phoneError) errors.contact = phoneError;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    // updateOrganization takes a JSON payload; send only filled fields.
    // Status and Plan are platform-owner-managed — the server silently strips
    // them from a non-platform update, so don't bother sending them.
    const input: Record<string, unknown> = {
      name: form.name.trim(),
    };
    if (form.industry.trim()) input.industry = form.industry.trim();
    if (form.country.trim()) input.country = form.country.trim();
    if (form.currency.trim()) input.currency = form.currency.trim();
    if (form.website.trim()) input.website = form.website.trim();
    if (form.contact.trim()) input.contact = form.contact.trim();
    // Org-wide display preferences (always sent — they have sensible defaults).
    input.timezone = form.timezone;
    input.dateformat = form.dateformat;
    input.timeFormat = form.timeFormat;

    try {
      await updateOrganization({ id: orgId, input }).unwrap();
      setMessage("Organization updated successfully.");
    } catch (err) {
      setError(
        (err as { message?: string })?.message ??
          "Failed to update organization.",
      );
    }
  };

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Organization setup</h2>
            <p className="panel__sub">
              {selected ? selected.ownerEmail : "Select an organization"}
            </p>
          </div>
          <div className="toolbar">
            <select
              className="select"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setMessage(null);
                setError(null);
                setFieldErrors({});
              }}
            >
              {organizations.length === 0 ? (
                <option value="">
                  {isLoading ? "Loading…" : "No organizations"}
                </option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {!orgId ? (
          <div className="profile-empty">
            No organization selected. Create one from the Platform Owner area first.
          </div>
        ) : (
          <>
            {message ? (
              <div
                className="form-error"
                style={{
                  background: "var(--success-soft)",
                  color: "#047857",
                }}
              >
                {message}
              </div>
            ) : null}
            {error ? <div className="form-error">{error}</div> : null}

            <div className="form-grid">
              <Field label="Organization name *" error={fieldErrors.name} full>
                <input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </Field>
              <label className="field field--full">
                Logo
                {uploadError ? <div className="form-error">{uploadError}</div> : null}
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  {selected?.logo ? (
                    <img
                      src={selected.logo}
                      alt="Organization logo"
                      style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
                      onClick={() => setPreviewOpen(true)}
                    />
                  ) : (
                    <span className="field__readonly">No logo yet</span>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: "none" }}
                    onChange={(e) => handleLogoFile(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading…" : "Upload logo"}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setGalleryOpen(true)}
                  >
                    Choose from gallery
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setEditorOpen(true)}
                    disabled={!selected?.logoKey}
                    title={!selected?.logoKey ? "Add a logo first" : "Crop, rotate and adjust"}
                  >
                    Edit logo
                  </button>
                </div>
                <GalleryPickerModal
                  open={galleryOpen}
                  onClose={() => setGalleryOpen(false)}
                  kind="image"
                  onSelect={handlePickLogoFromGallery}
                />
                <ImagePreviewModal
                  open={previewOpen}
                  onClose={() => setPreviewOpen(false)}
                  url={selected?.logo}
                  fileName="Organization logo"
                />
                <ImageEditorModal
                  open={editorOpen}
                  onClose={() => setEditorOpen(false)}
                  imageKey={selected?.logoKey ?? null}
                  url={selected?.logo ?? null}
                  fileName={`${selected?.name ?? "organization"} logo`}
                  title="Edit organization logo"
                  onApplied={handleLogoEdited}
                />
              </label>
              <label className="field">
                Status
                <input value={form.status} disabled title="Managed by the platform owner" />
                <span className="field__readonly">View only</span>
              </label>
              <label className="field">
                Plan
                <input
                  value={
                    plans.find((p) => p.id === form.planId)?.name ||
                    form.planId ||
                    "—"
                  }
                  disabled
                  title="Managed by the platform owner"
                />
                <span className="field__readonly">View only</span>
              </label>
              <label className="field">
                Industry
                <input
                  value={form.industry}
                  onChange={(e) => setField("industry", e.target.value)}
                  placeholder="Software"
                />
              </label>
              <label className="field">
                Country
                <input
                  value={form.country}
                  onChange={(e) => setField("country", e.target.value)}
                  placeholder="India"
                />
              </label>
              <label className="field">
                Currency
                <input
                  value={form.currency}
                  onChange={(e) => setField("currency", e.target.value)}
                  placeholder="INR"
                />
              </label>
              <label className="field">
                Timezone
                <select
                  value={form.timezone}
                  onChange={(e) => setField("timezone", e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Date format
                <select
                  value={form.dateformat}
                  onChange={(e) => setField("dateformat", e.target.value)}
                >
                  {DATE_FORMATS.map((fmt) => (
                    <option key={fmt} value={fmt}>
                      {fmt}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Time format
                <select
                  value={form.timeFormat}
                  onChange={(e) => setField("timeFormat", e.target.value)}
                >
                  <option value="24">24-hour (14:30)</option>
                  <option value="12">12-hour (2:30 PM)</option>
                </select>
              </label>
              <label className="field">
                Website
                <input
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  placeholder="https://…"
                />
              </label>
              <Field label="Contact" error={fieldErrors.contact} full>
                <PhoneInput
                  value={form.contact}
                  onChange={(v) => setField("contact", v)}
                />
              </Field>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default OrganizationSetup;
